import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { Seo, orgLd } from "@/components/site/Seo";
import { useSeo, useTexts } from "@/lib/layout";
import { LeadStory, StackStory, GridStory, SectionHeader, Reveal, Cover, Meta } from "@/components/site/Stories";
import { MostRead } from "@/components/site/MostRead";

const DarkBand = ({ section, kicker }) => (
  <section className="mt-24 bg-ink text-white py-16" data-testid={`home-section-${section.category.slug}`}>
    <div className="wrap">
      <SectionHeader title={section.category.name} kicker={kicker} to={`/seccion/${section.category.slug}`} dark />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0 lg:divide-x lg:divide-white/15">
        {section.articles.map((a, i) => (
          <Reveal key={a.id} delay={i * 0.08} className="lg:px-6 first:lg:pl-0 last:lg:pr-0">
            <Link to={`/noticia/${a.slug}`} className="group block">
              <Cover a={a} className="aspect-[4/3]" />
              <h3 className="font-display font-bold text-xl leading-snug mt-4 group-hover:text-gold transition-colors">{a.title}</h3>
              <p className="mt-2 text-[15px] text-slate-400 line-clamp-3">{a.summary}</p>
              <div className="mt-3"><Meta a={a} className="text-slate-500" /></div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CategorySection = ({ section, flip }) => {
  const [first, ...rest] = section.articles;
  return (
    <section className="wrap mt-20" data-testid={`home-section-${section.category.slug}`}>
      <SectionHeader title={section.category.name} to={`/seccion/${section.category.slug}`} />
      <div className={`grid grid-cols-12 gap-y-8 lg:gap-x-10 ${flip ? "lg:[direction:rtl]" : ""}`}>
        <Reveal className="col-span-12 lg:col-span-7 [direction:ltr]">
          <GridStory a={first} large testid={`section-${section.category.slug}-lead`} />
        </Reveal>
        <div className="col-span-12 lg:col-span-5 [direction:ltr]">
          {rest.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08}><StackStory a={a} index={`${section.category.slug}-${i}`} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Skeleton = () => (
  <div className="wrap pt-10 grid grid-cols-12 gap-10 animate-pulse" data-testid="home-loading">
    <div className="col-span-12 lg:col-span-7 space-y-5"><div className="aspect-[16/10] bg-muted" /><div className="h-12 bg-muted w-4/5" /><div className="h-5 bg-muted w-3/5" /></div>
    <div className="col-span-12 lg:col-span-5 space-y-6">{[0, 1, 2, 3].map((i) => <div key={i} className="h-24 bg-muted" />)}</div>
  </div>
);

const Empty = ({ t }) => (
  <div className="wrap py-28 max-w-3xl" data-testid="home-empty">
    <p className="kicker text-primary">{t.empty_kicker}</p>
    <h2 className="font-display font-black text-4xl sm:text-5xl mt-3 leading-tight">{t.empty_title}</h2>
    <p className="mt-5 text-lg text-muted-foreground">{t.empty_text}</p>
  </div>
);

const PolicyBox = ({ t }) => {
  if (!t.box_kicker && !t.box_text) return null;
  const cls = "kicker mt-4 inline-block hover:text-primary";
  const external = /^https?:/.test(t.box_link_url || "");
  return (
    <div className="border border-border bg-card p-6" data-testid="home-policy-box">
      <p className="kicker text-primary">{t.box_kicker}</p>
      <p className="font-display text-xl font-semibold mt-2 leading-snug">{t.box_text}</p>
      {t.box_link_label && t.box_link_url && (external
        ? <a href={t.box_link_url} className={cls} target="_blank" rel="noopener noreferrer" data-testid="home-policy-link">{t.box_link_label}</a>
        : <Link to={t.box_link_url} className={cls} data-testid="home-policy-link">{t.box_link_label}</Link>)}
    </div>
  );
};

export default function Home() {
  const { data, isLoading } = useQuery({ queryKey: ["home"], queryFn: () => get("/public/home"), refetchInterval: 120000 });
  const { seo, site } = useSeo();
  const t = useTexts("home");
  const origin = window.location.origin;
  const seoTag = (
    <Seo
      raw
      title={t.seo_title || seo.home_title || site}
      description={t.seo_description || seo.home_description}
      jsonLd={[orgLd(seo, site), { "@context": "https://schema.org", "@type": "WebSite", name: site, url: origin + "/", inLanguage: "es-ES",
        potentialAction: { "@type": "SearchAction", target: `${origin}/buscar?q={search_term_string}`, "query-input": "required name=search_term_string" } }]}
    />
  );
  if (isLoading) return <>{seoTag}<Skeleton /></>;
  if (!data?.lead) return <>{seoTag}<Empty t={t} /></>;
  return (
    <div data-testid="home-page">
      {seoTag}
      <h1 className="sr-only">{t.h1}</h1>
      <section className="wrap pt-8 md:pt-12">
        <div className="grid grid-cols-12 gap-y-10 lg:gap-x-10">
          <Reveal className="col-span-12 lg:col-span-7 lg:border-r lg:border-border lg:pr-10"><LeadStory a={data.lead} /></Reveal>
          <div className="col-span-12 lg:col-span-5">
            <p className="kicker text-muted-foreground mb-5 flex items-center gap-3"><span className="h-px w-8 bg-primary" />{t.top_label}</p>
            {data.top.map((a, i) => <Reveal key={a.id} delay={0.1 + i * 0.08}><StackStory a={a} index={i} /></Reveal>)}
          </div>
        </div>
      </section>

      {data.latest.length > 0 && (
        <section className="wrap mt-20 grid grid-cols-12 gap-y-14 lg:gap-x-12" data-testid="home-latest">
          <div className="col-span-12 lg:col-span-8">
            <SectionHeader title={t.latest_title} kicker={t.latest_kicker} />
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-12">
              {data.latest.map((a, i) => <Reveal key={a.id} delay={(i % 2) * 0.08}><GridStory a={a} testid={`latest-story-${i}`} /></Reveal>)}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-20 space-y-10">
              <MostRead items={data.most_read} />
              <PolicyBox t={t} />
            </div>
          </div>
        </section>
      )}

      {data.sections.map((s, i) =>
        s.category.slug === "analisis" ? <DarkBand key={s.category.slug} section={s} kicker={t.dark_kicker} /> : <CategorySection key={s.category.slug} section={s} flip={i % 2 === 1} />
      )}
    </div>
  );
}
